import { useEffect, useRef, useState } from 'react';
import type { FlightView as Trip, RailContent } from '@london-life/contracts';
import type { Character } from '@london-life/game/game';
import { createRide, type Ride, type RideView } from '@london-life/game/journey/ride';
import { seedOf } from '@london-life/game/journey/path';

// A train trip, as Lagos Life's coach out of Jibowu: the train itself fills the screen (on the platform, pulling
// out, along the viaducts past the stations, into the stop), with the trip's card at the top (the line and
// class, insured or not, from → to, how far is left and how long, the station passed or the next one, what it
// is like on board, what is happening) and three ways to watch: behind it, from the side or from your seat. A
// device that cannot draw it keeps the card over the map.
const VIEWS: { id: RideView; icon: string; name: string }[] = [
  { id: 'chase', icon: '🎥', name: 'Chase' },
  { id: 'side', icon: '👀', name: 'Side' },
  { id: 'inside', icon: '💺', name: 'Inside' },
];
const VIEW_KEY = 'll.train.view';
const rememberedView = (): RideView => {
  try {
    const saved = localStorage.getItem(VIEW_KEY);
    return saved === 'side' || saved === 'inside' ? saved : 'chase';
  } catch {
    return 'chase';
  }
};
const clock = (ms: number) => {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

// what the card says at a moment: the status, how far through (0–1), where the train is, the line from on board
export function trainStatus(trip: Trip, rail: RailContent, now: number) {
  const stop = rail.stops.find((item) => item.placeId === trip.to.placeId);
  const via = stop?.via ?? [];
  const km = stop?.km ?? 0;
  const boards = Date.parse(trip.boardsAt);
  const off = Date.parse(trip.takesOffAt);
  const arrives = Date.parse(trip.landsAt);
  if (now < boards)
    return {
      status: `Cab to ${rail.name} · ${Math.ceil((boards - now) / 1000)}s`,
      progress: 0,
      where: `🚕 On the way to ${rail.name}`,
      line: `🚕 On the way to ${rail.name} for the ${trip.jetName}`,
    };
  if (now < off)
    return {
      status: `Platform ${trip.gate} · doors close in ${Math.ceil((off - now) / 1000)}s`,
      progress: 0,
      where: `🚉 Platform ${trip.gate}`,
      line: rail.script.boarding,
    };
  if (now >= arrives)
    return {
      status: 'Arrived',
      progress: 1,
      where: `📍 ${trip.to.name}`,
      line: rail.script.arriving.replace('{stop}', trip.to.name),
    };
  const through = Math.min(1, (now - off) / Math.max(1, arrives - off));
  // the stations on the way, spread along it: the last one passed, or the next one coming
  const passed = Math.floor(through * (via.length + 1));
  const at = via[passed - 1];
  const nextIndex = Math.min(via.length, passed);
  const next = via[nextIndex] ?? trip.to.name;
  const nextKm = Math.max(1, Math.round(km * ((nextIndex + 1) / (via.length + 1) - through)));
  const said = [...rail.script.lines].sort((a, b) => a.at - b.at).filter((item) => item.at <= through);
  return {
    status: `${Math.max(1, Math.round(km * (1 - through)))} km to go`,
    progress: through,
    where:
      at && through * (via.length + 1) - passed < 0.4 ? `📍 Now passing: ${at}` : `➡️ Next: ${next} · ${nextKm} km`,
    line: said.length ? said[said.length - 1]!.text : rail.script.leaving,
  };
}

export function TrainScreen({
  trip,
  rail,
  character,
  night,
  dataSaver,
  icon,
}: {
  trip: Trip;
  rail: RailContent;
  character: Character;
  night: boolean;
  dataSaver: boolean;
  // the stop's icon, on its front
  icon: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const ride = useRef<Ride | null>(null);
  const [view, setView] = useState<RideView>(rememberedView);
  const [drawn, setDrawn] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  const latest = useRef({ view });
  latest.current = { view };
  // a player's company train (Rail Co.) in its own paint; otherwise the line's colours
  const line = trip.companyId ? undefined : rail.lines.find((item) => item.name === trip.jetName);
  const colours =
    line?.colours ??
    (trip.paint ? { body: trip.paint.body, band: trip.paint.stripe, doors: trip.paint.stripe } : undefined);
  const stop = rail.stops.find((item) => item.placeId === trip.to.placeId);
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const made = createRide(element, {
      mode: 'rail',
      train: colours,
      startedAt: Date.parse(trip.takesOffAt),
      endsAt: Date.parse(trip.landsAt),
      destination: { name: trip.to.name, icon },
      areas: [rail.name, ...(stop?.via ?? []), trip.to.name],
      character,
      night,
      seed: seedOf(trip.id),
      dataSaver,
      view: latest.current.view,
    });
    if (!made.ok) {
      setDrawn(false);
      return;
    }
    ride.current = made;
    return () => {
      made.dispose();
      ride.current = null;
    };
    // one ride per trip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.id]);
  useEffect(() => {
    ride.current?.setView(view);
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // remembered only where the browser allows it
    }
  }, [view]);
  const { status, progress, where, line: said } = trainStatus(trip, rail, now);
  const left = Date.parse(trip.landsAt) - Math.max(now, Date.parse(trip.takesOffAt));
  const comfort = line ? Math.min(3, line.comfort + (trip.cabin === 'first' ? 1 : 0)) : 1;
  const perks = line ? (trip.cabin === 'first' && line.first ? line.first.perks : line.perks) : '';
  return (
    <>
      {drawn && <div className="ride-screen train-screen" ref={box} />}
      <section className="flight-card train-card" aria-label={`Train ${trip.jetName} to ${trip.to.name}`} role="status">
        <div className="flight-card-top">
          <small>
            <i className="train-card-line" style={{ background: colours?.band }} aria-hidden="true" />
            {trip.companyId ? trip.jetName : `${trip.jetName} · ${trip.cabin === 'first' ? 'First Class' : 'Standard'}`}
            {trip.coverPence > 0 && <span aria-label="Insured"> 🛡️</span>}
          </small>
          <small>
            {trip.number} · {trip.registration}
          </small>
        </div>
        <div className="flight-card-route train-card-route">
          <strong>{trip.from.name}</strong>
          <span aria-hidden="true">→</span>
          <strong>{trip.to.name}</strong>
        </div>
        <div
          className="flight-card-bar"
          role="progressbar"
          aria-label="How far"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <i style={{ width: `${progress * 100}%` }} />
        </div>
        <div className="train-card-times">
          <small className="flight-card-status">{status}</small>
          <small>{clock(left)} left</small>
        </div>
        <strong className="train-card-where">{where}</strong>
        {perks && (
          <small className="train-card-perks">
            <span aria-label={`${comfort} of 3 stars`}>{'★'.repeat(comfort) + '☆'.repeat(3 - comfort)}</span> {perks}
          </small>
        )}
        <p className="flight-card-line">{said}</p>
      </section>
      {drawn && (
        <div className="flight-views" role="group" aria-label="How to watch">
          {VIEWS.map((item) => (
            <button type="button" key={item.id} aria-pressed={view === item.id} onClick={() => setView(item.id)}>
              <span aria-hidden="true">{item.icon}</span> {item.name}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
